// --- React ---
import React, { useState, useMemo } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { Card, Flex, ModalShell, Text, SummaryCard, CardTitle } from '@/components/ui';
import { Button, Input, Tag } from '@/components/ui';
import { DefaultContents } from '@/components/ui/common/DefaultContents';
import {
  RegistrationFormModal,
  type RegistrationFormData,
} from '@/components/modals/RegistrationFormModal';
import { DataTable } from '@/components/common/DataTable';

// --- API ---
import {
  useGetAllowedEmails,
  useCreateAllowedEmail,
  useUpdateAllowedEmail,
  useDeleteAllowedEmail,
} from '@/api/admin';

// --- 유효성 검사 · 에러 파싱 ---
import { getApiErrorKey } from '@/api/core';

// --- 스토어 ---
import { useNotificationStore } from '@/store/notificationStore';
import { useModalStore } from '@/store/modalStore';

// --- 타입 ---
import type {
  CreateAllowedEmailRequest,
  UpdateAllowedEmailRequest,
  AllowedEmail,
} from '@packages/types/admin/allowedEmail';
import { type ColumnDef } from '@tanstack/react-table';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

/** 가입 전 신규 CM 초대 — 이메일·회사·구독 계약 수정 가능 */
const isPendingCmContractInvite = (row: AllowedEmail): boolean =>
  !row.is_used && !row.company_id && (row.role_id == null || row.role_id === 2);

// ==================== 컴포넌트 ====================

const AllowedEmails: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { theme } = useTheme();
  const { showSuccess, showError } = useNotificationStore();
  const { showConfirm } = useModalStore();

  // ==================== API 훅 ====================

  const { data: emailsData, isLoading } = useGetAllowedEmails(false, 1, 100);
  const createMutation = useCreateAllowedEmail();
  const updateMutation = useUpdateAllowedEmail();
  const deleteMutation = useDeleteAllowedEmail();

  // ==================== 상태 관리 ====================

  // 추가 모달 상태
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [createDto, setCreateDto] = useState<CreateAllowedEmailRequest>({
    email: '',
    status: 'T',
    notes: '',
    company_name: '',
  });

  // 수정 모달 상태
  const [editingEmail, setEditingEmail] = useState<AllowedEmail | null>(null);
  const [updateDto, setUpdateDto] = useState<UpdateAllowedEmailRequest>({
    id: 0,
    notes: '',
    phone: '',
  });

  // ==================== 파생 데이터 ====================

  const emails = emailsData?.data || [];

  /** 초대 목록 통계 (전체/가입 완료/미가입) */
  const stats = useMemo(() => {
    const used = emails.filter((e: AllowedEmail) => e.is_used).length;
    const unused = emails.filter((e: AllowedEmail) => !e.is_used).length;

    return {
      total: emails.length,
      used,
      unused,
    };
  }, [emails]);

  // ==================== 핸들러 ====================

  /**
   * 허용 이메일 추가.
   * 필수: 이메일, 회사명, 계약 대수·개월.
   */
  const handleAddEmail = async (data: RegistrationFormData) => {
    if (!data.email) {
      showError(i18nKeys.auth.pleaseEnterEmail);
      return;
    }
    if (!data.company_name) {
      showError(i18nKeys.auth.pleaseEnterCompanyName);
      return;
    }
    if (data.planned_device_limit == null || data.planned_device_limit < 1) {
      showError(i18nKeys.auth.pleaseEnterPlannedDeviceLimit);
      return;
    }
    if (data.planned_duration_months == null || data.planned_duration_months < 1) {
      showError(i18nKeys.auth.pleaseEnterPlannedDurationMonths);
      return;
    }

    const createData: CreateAllowedEmailRequest = {
      email: data.email,
      status: 'T',
      notes: data.notes,
      company_name: data.company_name,
      role_id: 2,
      planned_device_limit: data.planned_device_limit,
      planned_duration_months: data.planned_duration_months,
    };

    try {
      await createMutation.mutateAsync(createData);
      showSuccess(i18nKeys.auth.emailAdded);
      setIsAddModalOpen(false);
      setCreateDto({
        email: '',
        status: 'T',
        notes: '',
        company_name: '',
      });
    } catch (error: unknown) {
      const errorKey = getApiErrorKey(error, i18nKeys.auth.emailAddFailed);
      showError(errorKey);
    }
  };

  /** 가입·구독 초대(미가입 CM) 수정 */
  const handleUpdateInvite = async (data: RegistrationFormData) => {
    if (!editingEmail) return;

    try {
      await updateMutation.mutateAsync({
        id: editingEmail.id,
        email: data.email,
        company_name: data.company_name,
        planned_device_limit: data.planned_device_limit,
        planned_duration_months: data.planned_duration_months,
        notes: data.notes || undefined,
      });
      showSuccess(i18nKeys.auth.memoUpdated);
      setEditingEmail(null);
    } catch (error: unknown) {
      const errorKey = getApiErrorKey(error, i18nKeys.auth.updateFailed);
      showError(errorKey);
    }
  };

  /** 이메일 메모/연락처 수정 (가입 완료 또는 SM 초대) */
  const handleUpdateEmail = async () => {
    if (!editingEmail) return;

    const payload: UpdateAllowedEmailRequest & { id: number } = {
      id: editingEmail.id,
      notes: updateDto.notes || undefined,
    };
    if (!editingEmail.is_used && updateDto.email && updateDto.email !== editingEmail.email) {
      payload.email = updateDto.email;
    }

    try {
      await updateMutation.mutateAsync(payload);
      showSuccess(i18nKeys.auth.memoUpdated);
      setEditingEmail(null);
      setUpdateDto({
        id: 0,
        notes: undefined,
        phone: undefined,
      });
    } catch (error: unknown) {
      const errorKey = getApiErrorKey(error, i18nKeys.auth.updateFailed);
      showError(errorKey);
    }
  };

  /** 이메일 삭제 (confirm 후 API 호출) */
  const handleDeleteEmail = (row: AllowedEmail) => {
    const message = row.is_used
      ? t(i18nKeys.auth.confirm_delete_used_allowed_email)
      : t(i18nKeys.auth.confirmDelete);
    showConfirm(
      t(i18nKeys.auth.delete),
      message,
      () => {
        void (async () => {
          try {
            await deleteMutation.mutateAsync(row.id);
            showSuccess(i18nKeys.auth.deleted);
          } catch (error: unknown) {
            const errorKey = getApiErrorKey(error, i18nKeys.auth.deleteFailed);
            showError(errorKey);
          }
        })();
      },
      undefined,
      'danger'
    );
  };

  // ==================== 테이블 컬럼 정의 ====================

  const columns = useMemo<ColumnDef<AllowedEmail>[]>(
    () => [
      {
        accessorKey: 'email',
        header: () => t(i18nKeys.auth.email),
        cell: ({ getValue }) => <Text size="sm">{getValue<string>()}</Text>,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: 'role_badge',
        header: () => t(i18nKeys.auth.role),
        cell: ({ row }) => {
          const email = row.original;
          const isSiteManagerInvite = email.role_id === 3;
          return (
            <Tag variant={isSiteManagerInvite ? 'info' : 'primary'} size="xs">
              {t(
                isSiteManagerInvite
                  ? i18nKeys.auth.role_badge_site_manager
                  : i18nKeys.auth.role_badge_company_manager
              )}
            </Tag>
          );
        },
      },
      {
        accessorKey: 'is_used',
        header: () => t(i18nKeys.auth.registrationStatus),
        enableColumnFilter: false,
        cell: ({ row }) => {
          const email = row.original;
          return (
            <Tag variant={email.is_used ? 'success' : 'warning'} size="xs">
              {email.is_used ? t(i18nKeys.auth.registered) : t(i18nKeys.auth.unregistered)}
            </Tag>
          );
        },
        enableSorting: true,
      },
      {
        id: 'verification_status',
        header: () => t(i18nKeys.auth.verificationStatus),
        cell: ({ row }) => {
          const email = row.original;
          return email.verification_status ? (
            <Flex direction="column" gap={1}>
              <Text size="xs" color="textSecondary">
                {t(i18nKeys.auth.emailSent)}{' '}
                {email.verification_status.latest_verification?.sent_at
                  ? email.verification_status.redis_status?.is_sent_active
                    ? t(i18nKeys.auth.completedActive)
                    : t(i18nKeys.auth.completedExpired)
                  : t(i18nKeys.auth.notSent)}
              </Text>
              <Text size="xs" color="textSecondary">
                {t(i18nKeys.auth.verificationStatusLabel)}{' '}
                {email.verification_status.latest_verification?.is_verified
                  ? email.verification_status.redis_status?.is_verified_active
                    ? t(i18nKeys.auth.completedActive)
                    : t(i18nKeys.auth.completedExpired)
                  : email.verification_status.latest_verification?.sent_at
                    ? t(i18nKeys.auth.waiting)
                    : '-'}
              </Text>
            </Flex>
          ) : (
            <Text size="xs" color="textSecondary">
              -
            </Text>
          );
        },
      },
      {
        id: 'planned_contract',
        header: () => t(i18nKeys.auth.allowed_email_planned_device_limit),
        cell: ({ row }) => {
          const email = row.original;
          if (email.company_id) {
            return (
              <Text size="xs" color="textSecondary">
                -
              </Text>
            );
          }
          const limit = email.planned_device_limit;
          const months = email.planned_duration_months;
          if (limit == null && months == null) {
            return (
              <Text size="xs" color="textSecondary">
                trial
              </Text>
            );
          }
          return (
            <Text size="sm">
              {limit ?? '-'} / {months != null ? `${months}mo` : '-'}
            </Text>
          );
        },
      },
      {
        accessorKey: 'notes',
        header: () => t(i18nKeys.auth.memo),
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const notes = getValue<string | null>();
          return (
            <Text size="sm" truncate style={{ maxWidth: '200px' }}>
              {notes || '-'}
            </Text>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: () => t(i18nKeys.auth.creationDate),
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const date = getValue<string>();
          return <Text size="sm">{new Date(date).toLocaleString('ko-KR')}</Text>;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'used_at',
        header: () => t(i18nKeys.auth.registrationDate),
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const date = getValue<string | null>();
          return <Text size="sm">{date ? new Date(date).toLocaleString('ko-KR') : '-'}</Text>;
        },
        enableSorting: true,
      },
      {
        id: 'actions',
        header: () => t(i18nKeys.auth.action),
        cell: ({ row }) => {
          const email = row.original;
          return (
            <Flex gap={4}>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingEmail(email);
                  if (isPendingCmContractInvite(email)) {
                    return;
                  }
                  setUpdateDto({
                    id: email.id,
                    email: email.email,
                    notes: email.notes || undefined,
                    phone: email.phone || undefined,
                  });
                }}
              >
                {t(
                  isPendingCmContractInvite(email)
                    ? i18nKeys.auth.editInvite
                    : i18nKeys.auth.editMemo
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={e => {
                  e.stopPropagation();
                  handleDeleteEmail(email);
                }}
              >
                {t(i18nKeys.auth.delete)}
              </Button>
            </Flex>
          );
        },
        enableSorting: false,
      },
    ],
    [theme, t]
  );

  // ==================== 렌더링 ====================

  return (
    <>
      <DefaultContents>
        {/* 통계 카드 */}
        <div data-tour="allowed-emails-summary">
          <SummaryCard
            items={[
              {
                label: t(i18nKeys.auth.total),
                value: stats?.total || 0,
                unit: null,
                decimal: null,
                state: null,
              },
              {
                label: t(i18nKeys.auth.registrationCompleted),
                value: stats?.used || 0,
                unit: null,
                decimal: null,
                state: 'success',
              },
              {
                label: t(i18nKeys.auth.unregistered),
                value: stats?.unused || 0,
                unit: null,
                decimal: null,
                state: 'warning',
              },
            ]}
          />
        </div>

        {/* 이메일 목록 테이블 */}
        <Card fullHeight fullWidth layout="flex" gap={4}>
          <Flex justify="between" align="center">
            <CardTitle>{t(i18nKeys.auth.allowedEmailList)}</CardTitle>
            <Button
              variant="primary"
              data-tour="allowed-emails-add"
              onClick={() => setIsAddModalOpen(true)}
            >
              + {t(i18nKeys.auth.addEmail)}
            </Button>
          </Flex>
          <div
            data-tour="allowed-emails-list"
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <DataTable
              data={emails}
              columns={columns}
              enablePagination={true}
              pageSize={20}
              enableSorting={true}
              enableFiltering={true}
              isLoading={isLoading}
              emptyMessage={t(i18nKeys.auth.noEmailsRegistered)}
            />
          </div>
        </Card>
      </DefaultContents>

      {/* ==================== 모달 ==================== */}

      {/* 이메일 추가 모달 */}
      <RegistrationFormModal
        isOpen={isAddModalOpen}
        mode="admin"
        title={t(i18nKeys.common.menu_allowed_emails)}
        description={t(i18nKeys.auth.signup_subscription_invite_email_notice)}
        initialData={{
          email: createDto.email,
          company_name: createDto.company_name,
          notes: createDto.notes,
        }}
        onClose={() => {
          setIsAddModalOpen(false);
          setCreateDto({
            email: '',
            status: 'T',
            notes: '',
            company_name: '',
          });
        }}
        onSubmit={handleAddEmail}
        isLoading={createMutation.isPending}
      />

      {/* 가입 전 CM 초대 수정 모달 */}
      <RegistrationFormModal
        isOpen={!!editingEmail && isPendingCmContractInvite(editingEmail)}
        mode="adminEdit"
        title={t(i18nKeys.auth.editInviteTitle)}
        initialData={
          editingEmail
            ? {
                email: editingEmail.email,
                company_name: editingEmail.company_name || '',
                notes: editingEmail.notes || '',
                planned_device_limit: editingEmail.planned_device_limit ?? 10,
                planned_duration_months: editingEmail.planned_duration_months ?? 12,
              }
            : undefined
        }
        onClose={() => setEditingEmail(null)}
        onSubmit={handleUpdateInvite}
        isLoading={updateMutation.isPending}
      />

      {/* 메모 수정 (가입 완료) / 미가입 SM 초대 */}
      <ModalShell
        isOpen={
          !!editingEmail && (editingEmail.is_used || !isPendingCmContractInvite(editingEmail))
        }
        title={t(i18nKeys.auth.editMemoAndContact)}
        onClose={() => setEditingEmail(null)}
        size="md"
        footer={
          <>
            <Button onClick={() => setEditingEmail(null)}>{t(i18nKeys.auth.cancel)}</Button>
            <Button variant="primary" onClick={handleUpdateEmail}>
              {t(i18nKeys.auth.save)}
            </Button>
          </>
        }
      >
        <Flex direction="column" gap={4}>
          <div>
            <Input
              label={t(i18nKeys.auth.email)}
              type="email"
              value={updateDto.email ?? editingEmail?.email ?? ''}
              onChange={value =>
                setUpdateDto(prev => ({
                  ...prev,
                  email: value,
                }))
              }
              disabled={!!editingEmail?.is_used}
              fullWidth
            />
          </div>

          {editingEmail?.is_used && (
            <div>
              <Input
                label={t(i18nKeys.auth.contact)}
                type="text"
                value={editingEmail?.phone || ''}
                onChange={() => {}}
                disabled
                fullWidth
              />
            </div>
          )}

          <div>
            <Input
              label={t(i18nKeys.auth.memo)}
              multiline
              rows={3}
              value={updateDto.notes || ''}
              onChange={value => setUpdateDto({ ...updateDto, notes: value || undefined })}
              placeholder={t(i18nKeys.auth.enterMemo)}
              fullWidth
            />
          </div>
        </Flex>
      </ModalShell>
    </>
  );
};

export default AllowedEmails;
